import axios from 'axios';
import attachAuthToken from './axiosConfig';
import {
  OrcamentoCilia,
  NotificacaoModulo,
  AuditoriaOrcamentoItem,
  AuditoriaTimelineCilia,
  AuditoriaItemCilia,
  AuditoriaConfiguracaoItem,
  ConfirmacaoVinculo,
  TipoOficina,
  TipoVeiculo,
} from '../interfaces/OrcamentoCilia';
import { clienteDoLogGeral } from './logGeral/clienteDoLogGeral';

/**
 * O Orçamento Cilia só existe na UI quando a env aponta para o backend dele.
 *
 * Mesmo mecanismo do Portal de Cotações (`PORTAL_COTACOES_HABILITADO`, at-73): o build de
 * produção injeta apenas `REACT_APP_BACKEND_URL` (`firebase-hosting-deploy.yml`), então em
 * prod a constante é `false` e nem o item de menu nem as 3 rotas são registrados.
 *
 * Sem isso, o módulo apareceria no menu em produção e **toda chamada iria para
 * `undefined/...`** — o `axios.create` abaixo não tem default, de propósito. Liberar em
 * produção é declarar a env no workflow de deploy, não mexer em código.
 */
export const ORCAMENTO_CILIA_HABILITADO = Boolean(process.env.REACT_APP_ORCAMENTO_CILIA_URL);

const orcamentoCiliaApi = axios.create({
  baseURL: process.env.REACT_APP_ORCAMENTO_CILIA_URL,
  withCredentials: true,
});

attachAuthToken(orcamentoCiliaApi);

/** Log geral deste serviço (transversal/at-91), lido pela tela Logs do sistema do SOAC. */
export const logGeralDeRequisicoes = clienteDoLogGeral(orcamentoCiliaApi);

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string') {
      return message;
    }
    return fallback;
  }
  return 'Erro desconhecido';
};

export interface PaginatedOrcamentoResponse {
  total: number;
  page: number;
  limit: number;
  data: OrcamentoCilia[];
}

/** Os filtros da lista de Requisições (at-18). Listas viram CSV no envio. */
export interface FiltrosRequisicoes {
  estados?: string[];
  /** Dia de importação, `AAAA-MM-DD`. Fim inclusivo. */
  de?: string;
  ate?: string;
  oficina?: string;
  importadoPor?: string;
  tipo?: 'INICIAL' | 'COMPLEMENTAR' | '';
  perdaTotal?: string[];
  atendimento?: 'COM' | 'SEM' | '';
  teste?: 'SO_TESTES' | 'OCULTAR' | '';
}

export interface GetOrcamentosParams extends FiltrosRequisicoes {
  page?: number;
  limit?: number;
  search?: string;
}

export interface ImportOrcamentoResponse {
  orcamento: OrcamentoCilia;
  avisos: string[];
}

export interface ConcluirOrcamentoResponse {
  orcamento: OrcamentoCilia;
  resultado: {
    existentes: number;
    criados: number;
    simulados: number;
    /**
     * Quantas requisições foram criadas no Nasajon (at-11). Normalmente **1**; numa versão com
     * peças de CAMINHO VIP e peças normais são **2** — o marcador que diz ao comprador para não
     * comprar é prefixo do "Motivo da Requisição", ou seja, vale para a requisição inteira,
     * enquanto comprar ou não é decisão por peça.
     *
     * ⚠️ A tela precisa dizer o número: quem concluiu tem de saber que existem dois documentos
     * a acompanhar no ERP, senão acompanha um e dá o outro por perdido.
     */
    requisicoesCriadas?: number;
    /**
     * Presente quando a conferência foi concluída **sem gerar requisição de compra**
     * (12/08/2026, `BUG-002`): a versão não trouxe peça comprável — complemento cujos itens
     * são todos herdados, ou peças fora do fluxo / aguardando cobertura. O orçamento avança;
     * o que não existe é requisição, e por isso a tela precisa dizer o motivo.
     *
     * O antigo `TUDO_EM_ESTOQUE` saiu em 20/08/2026 com a regra de estoque (ADR 0006).
     */
    semRequisicao?: 'NENHUM_ITEM_COMPRAVEL';
    /** Números das requisições criadas, na numeração do Nasajon (at-14). */
    codigos?: string[];
    /**
     * 🔴 Números que, logo depois da criação, o Nasajon mostrou repetidos em OUTRA requisição
     * (at-14): Compras criou uma no mesmo segundo. A tela avisa — o acompanhamento não se confunde
     * porque é feito pelo id do ERP.
     */
    codigosRepetidos?: string[];
    /** Para onde a requisição foi no ERP (at-14). */
    destino?: {
      tipoOficina: TipoOficina;
      tipoVeiculo: TipoVeiculo;
      centroCusto: string;
      classificacaoFinanceira: string;
      localEstoque: string;
    };
  };
}

/** A escolha do "Concluir e Enviar" que decide o local de uso e o de estoque (at-14, ADR 0009). */
export interface EscolhaDoDestino {
  tipoOficina: TipoOficina;
  tipoVeiculo: TipoVeiculo;
}

export interface SettingsDefaults {
  DEFAULT_UNIDADE: string;
  DEFAULT_GRUPO_INVENTARIO: string;
  DEFAULT_ORIGEM_MERCADORIA: string;
  DEFAULT_NCM: string;
  PMC_ALERTA_MARGEM_PCT: string;
  MEDIA_COMPRAS_MESES: string;
  /** at-15: teto de gasto mensal da cotação online, em dólares. Vazio ou zero = parada. */
  COTACAO_ONLINE_TETO_MENSAL_USD: string;
  // PEDIDO_* removidos em 29/07/2026 — pedido saiu do escopo do módulo
  /** at-14: destino da requisição no ERP e o cursor da numeração. */
  REQUISICAO_NUMERO_BASE: string;
  CNPJ_OFICINA_PROPRIA: string;
  LOCAL_USO_INTERNA_CARRO: string;
  LOCAL_USO_EXTERNA_CARRO: string;
  LOCAL_USO_INTERNA_MOTO: string;
  LOCAL_USO_EXTERNA_MOTO: string;
  LOCAL_ESTOQUE_INTERNA_CARRO: string;
  LOCAL_ESTOQUE_EXTERNA_CARRO: string;
  LOCAL_ESTOQUE_INTERNA_MOTO: string;
  LOCAL_ESTOQUE_EXTERNA_MOTO: string;
}

export interface ProdutoDeparaItem {
  codigoCilia: string;
  produtoNasajonId: string;
  codigoNasajon: string | null;
  especificacao: string | null;
  criadoPorId: string;
  updatedAt: string;
}

export interface GetProdutoDeparaParams {
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedProdutoDeparaResponse {
  total: number;
  page: number;
  limit: number;
  data: ProdutoDeparaItem[];
}

export type ProgressoImportacao =
  | { fase: 'vinculado'; placa: string; evento: string; totalItens: number }
  | { fase: 'consultando-erp'; atual: number; total: number }
  | { fase: 'salvando' };

/**
 * Importa o XML lendo o progresso em tempo real: a resposta é NDJSON (uma linha
 * por evento) e a última linha traz o resultado ou o erro. Usa `fetch` em vez do
 * axios porque só ele expõe o corpo da resposta em streaming.
 */
export const importOrcamento = async (
  file: File,
  onProgresso?: (p: ProgressoImportacao) => void,
  confirmacao?: ConfirmacaoVinculo,
  teste = false,
): Promise<ImportOrcamentoResponse> => {
  const formData = new FormData();
  // at-12: o campo vai ANTES do arquivo — o multer só popula req.body com o que chega antes.
  if (confirmacao) formData.append('confirmacao', confirmacao);
  // at-17: requisição de teste. Mesmo motivo: antes do arquivo.
  if (teste) formData.append('teste', 'true');
  formData.append('file', file);

  const token = localStorage.getItem('authToken');
  let response: Response;
  try {
    response = await fetch(`${process.env.REACT_APP_ORCAMENTO_CILIA_URL}/orcamentos/import`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  } catch {
    throw new Error('Não foi possível falar com o servidor. Verifique a conexão e tente de novo.');
  }

  // Falhas anteriores ao streaming continuam chegando como HTTP normal.
  if (!response.ok) {
    let corpo: any = null;
    try {
      corpo = await response.json();
    } catch {
      /* resposta sem corpo JSON */
    }
    if (corpo?.code) throw corpo;
    throw new Error(corpo?.message || 'Erro ao importar orçamento');
  }

  if (!response.body) throw new Error('Resposta inválida do servidor ao importar o orçamento.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let resultado: ImportOrcamentoResponse | null = null;

  const processarLinha = (linha: string) => {
    const texto = linha.trim();
    if (!texto) return;
    let evento: any;
    try {
      evento = JSON.parse(texto);
    } catch {
      return; // linha parcial/inesperada — ignora
    }
    if (evento.fase === 'fim') {
      resultado = evento.resultado;
    } else if (evento.fase === 'erro') {
      throw evento; // { code, message } — mesmo formato dos erros HTTP
    } else {
      onProgresso?.(evento as ProgressoImportacao);
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const linhas = buffer.split('\n');
    buffer = linhas.pop() ?? ''; // a última pode estar incompleta
    for (const linha of linhas) processarLinha(linha);
  }
  processarLinha(buffer);

  if (!resultado) {
    throw new Error('A importação terminou sem resposta do servidor — confira se o orçamento foi criado.');
  }
  return resultado;
};

/**
 * Erro do import que pede confirmação em vez de recusar (at-12): placa sem atendimento na
 * Oficina, ou com evento diferente. Reenviar o mesmo arquivo com `confirmacao` segue.
 */
export interface ErroImportComConfirmacao {
  code: 'ATENDIMENTO_NAO_ENCONTRADO' | 'EVENTO_DIVERGENTE';
  message: string;
  requerConfirmacao: true;
  placa?: string;
  eventoOficina?: string | null;
  eventoCilia?: string;
}

export const confirmacaoPedida = (erro: unknown): ConfirmacaoVinculo | null => {
  const e = erro as Partial<ErroImportComConfirmacao> | null;
  if (!e?.requerConfirmacao) return null;
  if (e.code === 'ATENDIMENTO_NAO_ENCONTRADO') return 'SEM_ATENDIMENTO';
  if (e.code === 'EVENTO_DIVERGENTE') return 'EVENTO_DIVERGENTE';
  return null;
};

export interface VincularPendentesResponse {
  vinculados: number;
  restantes: number;
  falhou: boolean;
}

/** Liga ao atendimento da Oficina as requisições que nasceram sem ele (at-12). */
export const vincularPendentes = async (): Promise<VincularPendentesResponse> => {
  try {
    const response = await orcamentoCiliaApi.post<VincularPendentesResponse>('/orcamentos/vincular-pendentes');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao ligar as requisições aos atendimentos da Oficina'));
  }
};

export const getOrcamentos = async (rawParams: GetOrcamentosParams = {}): Promise<PaginatedOrcamentoResponse> => {
  // Não enviar filtros vazios — o backend valida os enums. Lista vai em CSV: o axios mandaria
  // `campo[]=a&campo[]=b`, que o DTO não aceita (at-18).
  const params = Object.fromEntries(
    Object.entries(rawParams)
      .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v])
      .filter(([, v]) => v !== '' && v !== undefined && v !== null),
  );
  try {
    const response = await orcamentoCiliaApi.get<PaginatedOrcamentoResponse>('/orcamentos', { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar orçamentos'));
  }
};

export interface HistoricoOrcamentoResponse {
  numeroCilia: string;
  versoes: import('../interfaces/OrcamentoCilia').HistoricoVersao[];
}

// Histórico da cadeia (modal "Histórico do Orçamento"): todas as versões com
// as peças de cada uma — visão completa sem sair da tela atual.
export const getHistoricoOrcamento = async (id: string): Promise<HistoricoOrcamentoResponse> => {
  try {
    const response = await orcamentoCiliaApi.get<HistoricoOrcamentoResponse>(`/orcamentos/${id}/historico`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar o histórico do orçamento'));
  }
};

export const getOrcamentoById = async (id: string): Promise<OrcamentoCilia> => {
  try {
    const response = await orcamentoCiliaApi.get<OrcamentoCilia>(`/orcamentos/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar orçamento'));
  }
};

export const updateOrcamentoItem = async (
  id: string,
  itemId: string,
  data: { esteiraIdSoac?: string | null; esteiraNome?: string | null; codigoNasajon?: string | null }
): Promise<any> => {
  try {
    const response = await orcamentoCiliaApi.patch(`/orcamentos/${id}/itens/${itemId}`, data);
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError; // ex: PRODUTO_NAO_ENCONTRADO_NO_NASAJON / NASAJON_INDISPONIVEL
    }
    throw new Error(getErrorMessage(error, 'Erro ao atualizar item do orçamento'));
  }
};

export const concluirConferenca = async (
  id: string,
  escolha: EscolhaDoDestino,
): Promise<ConcluirOrcamentoResponse> => {
  try {
    const response = await orcamentoCiliaApi.post<ConcluirOrcamentoResponse>(`/orcamentos/${id}/concluir`, escolha);
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError;
    }
    throw new Error(getErrorMessage(error, 'Erro ao concluir conferência'));
  }
};

/**
 * Reconsulta no Nasajon a situação das requisições deste orçamento (at-11).
 *
 * ⚠️ Substituiu `registrarDesfecho`, o registro **manual** de aprovada/rejeitada. Com a requisição
 * criada por API o desfecho é **lido** do ERP, e manter a digitação permitiria marcar "aprovada"
 * aqui com a requisição reprovada lá.
 *
 * O mesmo caminho do job de 10 em 10 minutos, escopado a um orçamento: quem está com a tela aberta
 * não deve precisar esperar o ciclo para ver a aprovação que acabou de acontecer.
 */
export const atualizarRequisicoes = async (
  id: string,
): Promise<{ verificadas: number; atualizadas: number }> => {
  try {
    const response = await orcamentoCiliaApi.post(`/orcamentos/${id}/requisicoes/atualizar`);
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError; // ex: NASAJON_INDISPONIVEL
    }
    throw new Error(getErrorMessage(error, 'Erro ao consultar a situação da requisição no Nasajon'));
  }
};

export const reabrirOrcamento = async (id: string): Promise<any> => {
  try {
    const response = await orcamentoCiliaApi.post(`/orcamentos/${id}/reabrir`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao reabrir orçamento'));
  }
};

// ── Autorização de complementos (Chefe de Oficina, 29/07/2026) ──

export interface PaginatedAutorizacoesResponse {
  total: number;
  page: number;
  limit: number;
  data: OrcamentoCilia[];
}

export const getAutorizacoesPendentes = async (
  params: { page?: number; limit?: number } = {}
): Promise<PaginatedAutorizacoesResponse> => {
  try {
    const response = await orcamentoCiliaApi.get<PaginatedAutorizacoesResponse>('/orcamentos/autorizacoes/pendentes', {
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar complementos aguardando autorização'));
  }
};

export const getAutorizacoesPendentesCount = async (): Promise<number> => {
  try {
    const response = await orcamentoCiliaApi.get<{ count: number }>('/orcamentos/autorizacoes/pendentes/count');
    return response.data.count;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar contagem de autorizações pendentes'));
  }
};

export const decidirAutorizacao = async (
  id: string,
  data: { decisao: 'APROVADA' | 'NEGADA'; nota?: string }
): Promise<OrcamentoCilia> => {
  try {
    const response = await orcamentoCiliaApi.patch<OrcamentoCilia>(`/orcamentos/${id}/autorizacao`, data);
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError; // ex: NOTA_OBRIGATORIA / VERSAO_MAIS_NOVA_JA_IMPORTADA
    }
    throw new Error(getErrorMessage(error, 'Erro ao decidir a autorização'));
  }
};

// ── Notificações do módulo (badge/avisos no front compartilhado) ──

export const getMinhasNotificacoes = async (
  params: { page?: number; limit?: number; somenteNaoLidas?: boolean } = {}
): Promise<{ total: number; page: number; limit: number; data: NotificacaoModulo[] }> => {
  try {
    const response = await orcamentoCiliaApi.get('/notificacoes/mine', { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar notificações'));
  }
};

/** Quantos avisos do módulo estão sem ler — entra no badge do sino do topo (portal-cotacoes/at-14). */
export const getNotificacoesNaoLidasCount = async (): Promise<number> => {
  try {
    const response = await orcamentoCiliaApi.get('/notificacoes/unread-count');
    const d = response.data;
    return typeof d === 'number' ? d : Number(d?.count ?? d?.naoLidas ?? 0);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao contar notificações'));
  }
};

export const marcarNotificacaoLida = async (id: string): Promise<void> => {
  try {
    await orcamentoCiliaApi.patch(`/notificacoes/${id}/lida`);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao marcar notificação como lida'));
  }
};

export const getDefaults = async (): Promise<SettingsDefaults> => {
  try {
    const response = await orcamentoCiliaApi.get<SettingsDefaults>('/settings/defaults');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar configurações padrões'));
  }
};

export const updateDefault = async (key: string, value: string): Promise<any> => {
  try {
    const response = await orcamentoCiliaApi.put('/settings/defaults', { key, value });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao atualizar configuração padrão'));
  }
};

/**
 * A pausa das rotinas do backend de Requisições que chamam o Nasajon (transversal/at-99).
 * ⚠️ É outra pausa, não a do Dashboard de Compras: cada sistema tem a sua.
 */
export interface PausaNasajonCilia {
  pausado: boolean;
  por: string | null;
  em: string | null;
}

export const getPausaNasajon = async (): Promise<PausaNasajonCilia> => {
  try {
    const response = await orcamentoCiliaApi.get<PausaNasajonCilia>('/settings/nasajon-pausa');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao consultar a pausa das rotinas do Nasajon'));
  }
};

export const setPausaNasajon = async (pausado: boolean): Promise<PausaNasajonCilia> => {
  try {
    const response = await orcamentoCiliaApi.put<PausaNasajonCilia>('/settings/nasajon-pausa', { pausado });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao alterar a pausa das rotinas do Nasajon'));
  }
};

export interface ProdutoNasajonItem {
  nasajonId: string;
  idItem: string | null;
  codigo: string | null;
  especificacao: string;
  ncm: string | null;
}

// Busca AO VIVO no cadastro do ERP (28/07/2026 — o espelho local morreu).
export const searchProdutosNasajon = async (search: string): Promise<ProdutoNasajonItem[]> => {
  try {
    const response = await orcamentoCiliaApi.get<ProdutoNasajonItem[]>('/nasajon/produtos', {
      params: { search },
    });
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError; // ex: NASAJON_INDISPONIVEL
    }
    throw new Error(getErrorMessage(error, 'Erro ao buscar produtos do Nasajon'));
  }
};

export const reconhecerItem = async (
  id: string,
  itemId: string,
  produtoNasajonId: string | null,
  codigoNasajon?: string | null,
): Promise<any> => {
  try {
    const response = await orcamentoCiliaApi.patch(`/orcamentos/${id}/itens/${itemId}/reconhecer`, {
      produtoNasajonId,
      codigoNasajon,
    });
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError;
    }
    throw new Error(getErrorMessage(error, 'Erro ao reconhecer peça'));
  }
};

export interface MovimentacaoNasajon {
  id: string;
  data: string;
  sinal: string | null;
  pessoa?: string | null;
  quantidade: string;
  valor: string;
  valor_total: string | null;
  numero: string | null;
  historico: string | null;
  operacao_codigo: string | null;
  local_estoque_descricao?: string | null;
}

export interface HistoricoProdutoNasajon {
  produto: { codigo: string | null; especificacao: string; ncm: string | null };
  /**
   * Referência de PREÇO da peça no ERP (ADR 0006, 20/08/2026). O backend consulta a rota de
   * saldos do Nasajon porque é de lá que vem o PMC — a quantidade em estoque **não** sai na
   * resposta, e nenhuma tela do módulo a exibe.
   */
  referenciaPreco: { pmc: string | null; ultimaMovimentacao: string | null } | null;
  ultimaCompra: { data: string; valor: string } | null;
  extrato: { disponivel: boolean; movimentacoes: MovimentacaoNasajon[] };
}

export const getHistoricoProdutoNasajon = async (codigo: string): Promise<HistoricoProdutoNasajon> => {
  try {
    const response = await orcamentoCiliaApi.get<HistoricoProdutoNasajon>(
      `/nasajon/produtos/${encodeURIComponent(codigo)}/historico`,
    );
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError;
    }
    throw new Error(getErrorMessage(error, 'Erro ao buscar o histórico da peça no Nasajon'));
  }
};

// O fluxo de PEDIDO DE COMPRA (envio ao ERP + busca de fornecedores) foi
// REMOVIDO em 29/07/2026 (decisão do Victor): o módulo é de SOLICITAÇÕES —
// pedido é etapa posterior da governança de compras, fora deste app.

export const reverificarOrcamento = async (
  id: string,
): Promise<{ verificados: number; existentes: number; aCriar: number }> => {
  try {
    const response = await orcamentoCiliaApi.post(`/orcamentos/${id}/reverificar`);
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) {
      throw apiError; // ex: NASAJON_INDISPONIVEL
    }
    throw new Error(getErrorMessage(error, 'Erro ao reverificar produtos no Nasajon'));
  }
};

// `forcarCompraItem` foi REMOVIDA em 20/08/2026 (ADR 0006): ela era o escape da regra de
// estoque, e a rota do backend deixou de existir junto com a regra.

// `downloadArquivoSolicitacao` foi REMOVIDA na at-11 (18/09/2026): o CSV existia para o comprador
// lançar a solicitação à mão no ERP, e a requisição passou a ser criada por API. Manter o download
// conservaria um caminho paralelo que produziria requisição duplicada — a rota do backend também
// deixou de existir.

// ── Unidades de medida e usuários do Nasajon (at-11) ──

export interface UnidadeNasajon {
  id: string;
  codigo: string;
  descricao: string | null;
}

/**
 * As unidades do cadastro do ERP, para o seletor da conferência.
 *
 * ⚠️ **Sem catch silencioso**: lista vazia por erro seria lida como "o ERP não tem unidades" e o
 * orçamentista ficaria sem entender por que não consegue escolher. O backend já responde 503 quando
 * não consegue consultar; quem trata é a tela.
 */
export const getUnidadesNasajon = async (): Promise<UnidadeNasajon[]> => {
  try {
    const response = await orcamentoCiliaApi.get<UnidadeNasajon[]>('/nasajon/unidades');
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) throw apiError;
    throw new Error(getErrorMessage(error, 'Erro ao carregar as unidades do Nasajon'));
  }
};

export interface UsuarioNasajon {
  id: string;
  login: string | null;
  name: string | null;
  email: string | null;
}

/** Busca usuários do ERP pelo nome/e-mail — alimenta o vínculo manual da tela de configuração. */
export const searchUsuariosNasajon = async (search: string): Promise<UsuarioNasajon[]> => {
  try {
    const response = await orcamentoCiliaApi.get<UsuarioNasajon[]>('/nasajon/usuarios', {
      params: { search },
    });
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) throw apiError;
    throw new Error(getErrorMessage(error, 'Erro ao buscar usuários no Nasajon'));
  }
};

// ── De-para de usuário: quem assina a requisição no ERP (at-11, ADR 0007 decisão 3) ──

export interface UsuarioDeparaItem {
  userIdSoac: string;
  nomeSoac: string | null;
  nasajonUserId: string;
  nasajonLogin: string | null;
  nasajonNome: string | null;
  nasajonEmail: string | null;
  /** at-13: quem criou o vínculo. Backend anterior à at-13 não manda o campo. */
  origem?: 'MANUAL' | 'AUTOMATICO_EMAIL';
  updatedAt: string;
}

/** Rótulo da origem do vínculo — um só, usado na configuração do módulo e no modal de usuários. */
export const rotuloOrigemDoVinculo = (origem?: UsuarioDeparaItem['origem']): string =>
  origem === 'AUTOMATICO_EMAIL' ? 'Automático (e-mail)' : 'Manual';

export const getUsuarioDepara = async (): Promise<UsuarioDeparaItem[]> => {
  try {
    const response = await orcamentoCiliaApi.get<UsuarioDeparaItem[]>('/usuario-depara');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao carregar os vínculos de usuário'));
  }
};

/**
 * Vincula uma pessoa do SOAC a uma credencial do Nasajon.
 *
 * ⚠️ Só o **id** do usuário do ERP é enviado: nome, login e e-mail são relidos lá pelo backend. O
 * rótulo que está na tela pode ser de um cache do navegador, e gravá-lo faria a configuração
 * mostrar um nome que não corresponde à credencial.
 */
export const vincularUsuarioNasajon = async (data: {
  userIdSoac: string;
  nomeSoac?: string | null;
  nasajonUserId: string;
}): Promise<UsuarioDeparaItem> => {
  const { userIdSoac, ...corpo } = data;
  try {
    const response = await orcamentoCiliaApi.put<UsuarioDeparaItem>(`/usuario-depara/${userIdSoac}`, corpo);
    return response.data;
  } catch (error: any) {
    const apiError = error.response?.data;
    if (apiError && apiError.code) throw apiError; // ex: USUARIO_NASAJON_JA_VINCULADO
    throw new Error(getErrorMessage(error, 'Erro ao vincular o usuário do Nasajon'));
  }
};

/**
 * Por que a conta de quem está logado não ficou vinculada ao Nasajon (orcamento-cilia/at-13).
 * `NASAJON_INDISPONIVEL` NÃO é problema de cadastro — a tela diz que não conseguiu conferir.
 */
export type MotivoSemVinculo =
  | 'SEM_EMAIL'
  | 'SEM_CORRESPONDENCIA'
  | 'AMBIGUO'
  | 'JA_VINCULADO_A_OUTRO'
  | 'NASAJON_INDISPONIVEL';

export interface MeuVinculoNasajon {
  vinculo: { nasajonUserId: string; nasajonNome: string | null; nasajonLogin: string | null } | null;
  motivo: MotivoSemVinculo | null;
  criadoAgora: boolean;
}

/**
 * O vínculo de quem está logado — o backend tenta criá-lo pelo e-mail quando ainda não existe.
 * Sem catch: "não vinculada" e "não consegui perguntar" levam a leituras opostas, e quem as
 * distingue é a tela.
 */
export const getMeuVinculoNasajon = async (): Promise<MeuVinculoNasajon> => {
  const response = await orcamentoCiliaApi.get<MeuVinculoNasajon>('/usuario-depara/meu');
  return response.data;
};

export const removerUsuarioNasajon = async (userIdSoac: string): Promise<void> => {
  try {
    await orcamentoCiliaApi.delete(`/usuario-depara/${userIdSoac}`);
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao remover o vínculo de usuário'));
  }
};

export const getProdutoDepara = async (
  rawParams: GetProdutoDeparaParams = {},
): Promise<PaginatedProdutoDeparaResponse> => {
  // Não enviar filtros vazios (mesmo padrão do getOrcamentos).
  const params = Object.fromEntries(
    Object.entries(rawParams).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  );
  try {
    const response = await orcamentoCiliaApi.get<PaginatedProdutoDeparaResponse>('/produto-depara', { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar de-para de produtos'));
  }
};

export const deleteProdutoDepara = async (codigoCilia: string): Promise<any> => {
  try {
    const response = await orcamentoCiliaApi.delete(`/produto-depara/${codigoCilia}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao excluir de-para de produto'));
  }
};

// ── Auditoria do módulo (at-05, 16/08/2026) ────────────────────────────────────
//
// Alimenta a aba "Orçamentos" da tela de Auditoria do SOAC. A humanização (título,
// detalhes e o que cada evento abre) vem PRONTA do backend do módulo — aqui não há mapa
// de `action`, pelo mesmo motivo da aba de Cotações: quem sabe o que uma ação significa é
// quem a grava.

export interface PaginatedAuditoriaOrcamentos {
  data: AuditoriaOrcamentoItem[];
  total: number;
  page: number;
  limit: number;
}

export interface PaginatedAuditoriaConfiguracao {
  data: AuditoriaConfiguracaoItem[];
  total: number;
  page: number;
  limit: number;
}

export const fetchAuditoriaOrcamentos = async (
  busca?: string,
  page = 1,
  limit = 10,
): Promise<PaginatedAuditoriaOrcamentos> => {
  try {
    const response = await orcamentoCiliaApi.get<PaginatedAuditoriaOrcamentos>('/auditoria/orcamentos', {
      params: { busca: busca || undefined, page, limit },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar orçamentos para auditoria'));
  }
};

export const fetchAuditoriaTimelineOrcamento = async (id: string): Promise<AuditoriaTimelineCilia> => {
  try {
    const response = await orcamentoCiliaApi.get<AuditoriaTimelineCilia>(`/auditoria/orcamentos/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao carregar a auditoria do orçamento'));
  }
};

export const fetchAuditoriaItem = async (itemId: string): Promise<AuditoriaItemCilia> => {
  try {
    const response = await orcamentoCiliaApi.get<AuditoriaItemCilia>(`/auditoria/itens/${itemId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao carregar o histórico da peça'));
  }
};

export const fetchAuditoriaConfiguracoes = async (
  page = 1,
  limit = 20,
): Promise<PaginatedAuditoriaConfiguracao> => {
  try {
    const response = await orcamentoCiliaApi.get<PaginatedAuditoriaConfiguracao>('/auditoria/configuracoes', {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao carregar o histórico de alterações'));
  }
};

// ─── Cotação online de peças (orcamento-cilia/at-15) ────────────────────────────
// Sem catch silencioso em nenhuma: "nenhum preço encontrado" e "não consegui perguntar" levam
// Compras a conclusões opostas, e quem distingue é a tela.

export type CanalCotacaoOnline = 'CONCESSIONARIAS' | 'MERCADO_LIVRE' | 'FERRO_VELHO' | 'PARALELAS' | 'LOJAS_AUTOPECAS';
export type NivelCotacaoOnline = 'ECONOMICO' | 'CUIDADOSO';

export interface OfertaCotacaoOnline {
  canal: CanalCotacaoOnline;
  preco: number | null;
  url?: string | null;
  titulo?: string | null;
  vendedor?: string | null;
  cidadeUf?: string | null;
  condicao?: string | null;
  kit?: boolean;
  compatibilidade?: 'COMPATIVEL' | 'CONFERIR' | 'NAO_SERVE' | null;
  oQueConferir?: string | null;
  evidencia?: string | null;
  cnpj?: string | null;
  dominio?: string | null;
  credenciado?: boolean | null;
  semOfertaMotivo?: string | null;
  /** Anúncios abertos que não viraram a oferta, com link e motivo. Ausente em pesquisa antiga. */
  anunciosDescartados?: Array<{ url: string; titulo: string | null; preco: number | null; motivo: string }> | null;
}

export interface EstadoCotacaoOnline {
  configurada: boolean;
  tetoMensalUsd: number | null;
  gastoNoMesUsd: number;
  motivoParado: string | null;
}

export interface ItemCotacaoOnline {
  itemId: string;
  codigoCilia: string;
  descricao: string;
  quantidade: number;
  emCurso: {
    status: 'PENDENTE' | 'EM_ANDAMENTO';
    nivel: NivelCotacaoOnline;
    criadaEm: string;
    iniciadaEm?: string | null;
    /** Etapa informada pelo backend (BUSCANDO, LENDO, JULGANDO). Nula na fila e em servidor antigo. */
    etapa?: 'BUSCANDO' | 'LENDO' | 'JULGANDO' | null;
    etapaDetalhe?: string | null;
  } | null;
  ultimaFalha: { erro: string | null; em: string } | null;
  resultado: {
    cotacaoId: string;
    nivel: NivelCotacaoOnline;
    origem: 'AUTOMATICA' | 'REBUSCA';
    pesquisadaEm: string;
    disparadaPorNome: string | null;
    menorPreco: { preco: number; canal: CanalCotacaoOnline; url: string | null } | null;
    ofertas: OfertaCotacaoOnline[];
  } | null;
  totalDePesquisas: number;
  /**
   * Por que o "Cotar peças online" não cota esta peça: `REPARO` (o XML não marca troca, não há o
   * que comprar) ou `VIP` (comprada na rua). Ausente em servidor antigo.
   */
  foraDaCotacao?: 'REPARO' | 'VIP' | null;
}

/** Perda total (at-15): a cotação contra 70% da FIPE, nas cestas de menor e de maior valor. */
export type SituacaoPerdaTotal = 'PT_NAS_DUAS' | 'PT_SO_NA_MAIOR' | 'ABAIXO' | 'SEM_FIPE' | 'SEM_PRECO';

export interface PerdaTotal {
  situacao: SituacaoPerdaTotal;
  limite: number;
  fipe: number | null;
  fipeMotivo: string | null;
  fipeVeiculo: string | null;
  fipeMesReferencia: string | null;
  servicos: number;
  cestaMenor: number;
  cestaMaior: number;
  percentualMenor: number | null;
  percentualMaior: number | null;
  pecasComPreco: number;
  pecasSemPreco: number;
  pecasPesquisando: number;
  calculadoEm: string | null;
}

export interface ResultadoCotacaoOnline {
  estado: EstadoCotacaoOnline;
  orcamento: { id: string; placa: string };
  /** Nulo enquanto a requisição não foi concluída (o cálculo começa no "Concluir e enviar"). */
  perdaTotal?: PerdaTotal | null;
  itens: ItemCotacaoOnline[];
}

export const fetchCotacaoOnline = async (orcamentoId: string): Promise<ResultadoCotacaoOnline> => {
  try {
    const response = await orcamentoCiliaApi.get<ResultadoCotacaoOnline>(`/orcamentos/${orcamentoId}/cotacao-online`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível carregar a cotação online'));
  }
};

export const fetchEstadoCotacaoOnline = async (): Promise<EstadoCotacaoOnline> => {
  try {
    const response = await orcamentoCiliaApi.get<EstadoCotacaoOnline>('/cotacao-online/estado');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível consultar o consumo da cotação online'));
  }
};

/** O botão "Cotar peças online": cota as peças a comprar ainda não pesquisadas da requisição. */
export const cotarRequisicaoOnline = async (
  orcamentoId: string,
  opcoes: { nivel: NivelCotacaoOnline; recotarSemPreco: boolean },
): Promise<{ enfileiradas: number; jaPesquisadas: number; recotadas: number; emCurso: number }> => {
  try {
    const { data } = await orcamentoCiliaApi.post(`/orcamentos/${orcamentoId}/cotacao-online`, opcoes);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível iniciar a cotação online'));
  }
};

export const rebuscarCotacaoOnline = async (itemId: string, nivel: NivelCotacaoOnline): Promise<void> => {
  try {
    await orcamentoCiliaApi.post(`/orcamentos/itens/${itemId}/cotacao-online`, { nivel });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível pedir a nova pesquisa'));
  }
};

export const baixarPlanilhaCotacaoOnline = async (orcamentoId: string): Promise<Blob> => {
  try {
    const response = await orcamentoCiliaApi.get(`/orcamentos/${orcamentoId}/cotacao-online/planilha`, { responseType: 'blob' });
    return response.data as Blob;
  } catch (error) {
    // Com `responseType: blob` a mensagem do servidor vem dentro do Blob: o texto fixo é o que chega.
    throw new Error('Não foi possível gerar a planilha da cotação online');
  }
};

export interface DesmonteCredenciado {
  id: string;
  nome: string;
  cnpj: string | null;
  dominio: string | null;
  confirmadoPorNome: string | null;
  confirmadoEm: string;
}

export const confirmarDesmonteCredenciado = async (dados: { nome: string; cnpj?: string | null; dominio?: string | null }): Promise<DesmonteCredenciado> => {
  try {
    const response = await orcamentoCiliaApi.post<DesmonteCredenciado>('/desmontes-credenciados', {
      nome: dados.nome,
      ...(dados.cnpj ? { cnpj: dados.cnpj } : {}),
      ...(dados.dominio ? { dominio: dados.dominio } : {}),
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível registrar o credenciamento'));
  }
};

// ─── Consumo da cotação online (tela Consumo de IA, ADMIN e TI) ────────────────

export interface TotaisCotacaoOnline {
  pesquisas: number;
  buscas: number;
  tokensEntrada: number;
  tokensSaida: number;
  custoUsd: number;
}

export interface ResumoConsumoCotacaoOnline {
  janelaDias: number;
  hoje: TotaisCotacaoOnline;
  mesCorrente: TotaisCotacaoOnline;
  ultimos30Dias: TotaisCotacaoOnline;
  porMes: Array<{ mes: string } & TotaisCotacaoOnline>;
  porUsuario: Array<{ usuarioId: string | null; nome: string | null } & TotaisCotacaoOnline>;
}

export interface PesquisaDoConsumo {
  id: string;
  criadaEm: string;
  concluidaEm: string | null;
  disparadaPorNome: string | null;
  origem: 'AUTOMATICA' | 'REBUSCA';
  nivel: NivelCotacaoOnline;
  status: 'PENDENTE' | 'EM_ANDAMENTO' | 'CONCLUIDA' | 'FALHOU';
  orcamentoId: string;
  peca: { codigo: string; descricao: string } | null;
  placa: string | null;
  numeroCilia: string | null;
  buscas: number;
  tokensEntrada: number;
  tokensSaida: number;
  custoUsd: number;
  /** Menor preço entre os canais (só na lista da Auditoria e do consumo; nulo = nenhum serviu). */
  menorPreco?: { preco: number; canal: CanalCotacaoOnline } | null;
  erro?: string | null;
}

export interface PaginaDePesquisas {
  pagina: number;
  porPagina: number;
  total: number;
  itens: PesquisaDoConsumo[];
}

export const fetchConsumoCotacaoOnline = async (): Promise<ResumoConsumoCotacaoOnline> => {
  try {
    const response = await orcamentoCiliaApi.get<ResumoConsumoCotacaoOnline>('/cotacao-online/consumo');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível consultar o consumo da cotação online'));
  }
};

export const fetchPesquisasDoConsumo = async (pagina: number, porPagina = 20): Promise<PaginaDePesquisas> => {
  try {
    const response = await orcamentoCiliaApi.get<PaginaDePesquisas>('/cotacao-online/consumo/pesquisas', {
      params: { pagina, porPagina },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível consultar as pesquisas da cotação online'));
  }
};

// ─── Aba "Cotação online" da Auditoria ─────────────────────────────────────────

export interface DetalheDaPesquisa {
  id: string;
  orcamentoId: string;
  criadaEm: string;
  concluidaEm: string | null;
  disparadaPorNome: string | null;
  origem: 'AUTOMATICA' | 'REBUSCA';
  nivel: NivelCotacaoOnline;
  status: PesquisaDoConsumo['status'];
  erro: string | null;
  buscas: number;
  custoUsd: number;
  peca: { codigo: string; descricao: string; quantidade: number } | null;
  placa: string | null;
  numeroCilia: string | null;
  ofertas: OfertaCotacaoOnline[];
}

export const fetchAuditoriaCotacaoOnline = async (busca: string, pagina: number, porPagina = 20): Promise<PaginaDePesquisas> => {
  try {
    const response = await orcamentoCiliaApi.get<PaginaDePesquisas>('/auditoria/cotacao-online', {
      params: { pagina, porPagina, ...(busca.trim() ? { busca: busca.trim() } : {}) },
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível consultar as cotações online'));
  }
};

export const fetchDetalheCotacaoOnline = async (id: string): Promise<DetalheDaPesquisa> => {
  try {
    const response = await orcamentoCiliaApi.get<DetalheDaPesquisa>(`/auditoria/cotacao-online/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Não foi possível abrir a pesquisa'));
  }
};
